// PlaybackSessionTracker — web mirror of the iOS PlaybackSessionTracker.
//
// Implements the session model defined in
// ../../../demodeck-backend/docs/PLAYBACK_TELEMETRY.md.
//
// For authenticated listeners (firebase.auth().currentUser != null),
// threshold-cross writes plays/{uid}/{count,lastPlayedAt,totalMs}
// directly to RTDB. Anonymous share-link listeners flow through the
// logSharedTrackPlay Cloud Function instead (see SharedPlayer.js).

import firebase from '../firebaseConfig';

const SessionEndReason = {
    trackChanged: 'track_changed',
    restartIntent: 'restart_intent',
    pausedIdle: 'paused_idle',
    exit: 'exit',
    completed: 'completed',
};

const MAX_NATURAL_TICK_DELTA_SEC = 2.0;
const RESTART_INTENT_MAX_START_SEC = 2.0;
const RESTART_INTENT_MIN_FROM_SEC = 2.0;
const PAUSED_IDLE_SEC = 300;

const IS_DEV = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

export function newSessionId() {
    return (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

class PlaybackSession {
    constructor({ trackId, revisionId, artistId, projectId, durationMs }) {
        this.sessionId = newSessionId();
        this.trackId = trackId;
        this.revisionId = revisionId;
        this.artistId = artistId;
        this.projectId = projectId;
        this.durationMs = durationMs;
        this.thresholdMs = Math.min(30_000, Math.floor(durationMs / 2));
        this.startedAt = Date.now();
        this.forwardListenedMs = 0;
        this.uniqueSecondsListened = new Set();
        this.lastInsertedSecond = -1;
        this.maxPositionMs = 0;
        this.countedAsPlay = false;
    }

    static fromTrack(track, durationSeconds) {
        return new PlaybackSession({
            trackId: track.id,
            revisionId: track.currRevId,
            artistId: track.project?.artistId || track.project?.artist?.id,
            projectId: track.project?.id,
            durationMs: Math.max(0, Math.floor((durationSeconds || 0) * 1000)),
        });
    }

    cloneForRestart() {
        return new PlaybackSession({
            trackId: this.trackId,
            revisionId: this.revisionId,
            artistId: this.artistId,
            projectId: this.projectId,
            durationMs: this.durationMs,
        });
    }

    accumulateForward(deltaMs, atSecond) {
        if (deltaMs <= 0) return false;
        this.forwardListenedMs += deltaMs;
        if (atSecond !== this.lastInsertedSecond) {
            this.uniqueSecondsListened.add(atSecond);
            this.lastInsertedSecond = atSecond;
        }
        this.maxPositionMs = Math.max(this.maxPositionMs, atSecond * 1000);
        if (!this.countedAsPlay && this.forwardListenedMs >= this.thresholdMs) {
            this.countedAsPlay = true;
            return true;
        }
        return false;
    }

    updateMaxPosition(currentMs) {
        this.maxPositionMs = Math.max(this.maxPositionMs, currentMs);
    }
}

class PlaybackSessionTracker {
    constructor() {
        this.currentSession = null;
        this.lastObservedSeconds = null;
        this.pausedAt = null;
    }

    startSession(track, durationSeconds) {
        this.endSession(SessionEndReason.trackChanged);
        if (!track || !track.id || !track.currRevId) return;
        const session = PlaybackSession.fromTrack(track, durationSeconds);
        this.currentSession = session;
        this.lastObservedSeconds = null;
        this._log('session start', {
            sessionId: session.sessionId.slice(0, 8),
            track: track.id,
            durationMs: session.durationMs,
            thresholdMs: session.thresholdMs,
        });
    }

    endSession(reason) {
        const session = this.currentSession;
        if (!session) return;
        this._log('session end', {
            sessionId: session.sessionId.slice(0, 8),
            reason,
            forwardMs: session.forwardListenedMs,
            uniqueSec: session.uniqueSecondsListened.size,
            counted: session.countedAsPlay,
        });
        this.currentSession = null;
        this.lastObservedSeconds = null;
        this.pausedAt = null;
    }

    timeObserverTick(currentSec) {
        const session = this.currentSession;
        if (!session) return;

        const last = this.lastObservedSeconds;
        this.lastObservedSeconds = currentSec;

        if (last == null) {
            session.updateMaxPosition(Math.floor(currentSec * 1000));
            return;
        }

        if (currentSec < RESTART_INTENT_MAX_START_SEC && last >= RESTART_INTENT_MIN_FROM_SEC) {
            this._restartSessionForSameRevision(session, SessionEndReason.restartIntent);
            return;
        }

        const delta = currentSec - last;
        const intSec = Math.floor(currentSec);

        if (delta > 0 && delta <= MAX_NATURAL_TICK_DELTA_SEC) {
            const crossed = session.accumulateForward(Math.floor(delta * 1000), intSec);
            if (crossed) this._fireCountedPlay(session);
        } else {
            session.updateMaxPosition(Math.floor(currentSec * 1000));
        }
    }

    paused() {
        if (!this.currentSession) return;
        this.pausedAt = Date.now();
    }

    resumed() {
        this.lastObservedSeconds = null;
        const session = this.currentSession;
        const pAt = this.pausedAt;
        this.pausedAt = null;
        if (!session || !pAt) return;
        const gapSec = (Date.now() - pAt) / 1000;
        if (gapSec < PAUSED_IDLE_SEC) return;
        this._restartSessionForSameRevision(
            session,
            SessionEndReason.pausedIdle,
            `resumed_after_idle gap=${Math.floor(gapSec)}s`,
        );
    }

    didCompletePlayback() {
        this.endSession(SessionEndReason.completed);
    }

    didStopPlayback() {
        this.endSession(SessionEndReason.exit);
    }

    _restartSessionForSameRevision(prev, reason, logSuffix) {
        const next = prev.cloneForRestart();
        this.endSession(reason);
        this.currentSession = next;
        this.lastObservedSeconds = null;
        this._log('session start', {
            sessionId: next.sessionId.slice(0, 8),
            track: next.trackId,
            reason: logSuffix || reason,
        });
    }

    _fireCountedPlay(session) {
        this._log('session counted', {
            sessionId: session.sessionId.slice(0, 8),
            forwardMs: session.forwardListenedMs,
            thresholdMs: session.thresholdMs,
        });
        const uid = firebase.auth().currentUser?.uid;
        if (!uid) return;
        if (!session.artistId || !session.projectId || !session.trackId || !session.revisionId) return;
        const base = `artists/${session.artistId}/projects/${session.projectId}/tracks/${session.trackId}/revisions/${session.revisionId}/metadata/plays/${uid}`;
        const inc = firebase.database.ServerValue.increment;
        const ts = firebase.database.ServerValue.TIMESTAMP;
        firebase.database().ref().update({
            [`${base}/count`]: inc(1),
            [`${base}/lastPlayedAt`]: ts,
            [`${base}/totalMs`]: inc(session.forwardListenedMs),
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[PlaybackSessionTracker] failed to write counted play', err);
        });
    }

    _log(tag, fields) {
        if (!IS_DEV) return;
        const flat = Object.entries(fields).map(([k, v]) => `${k}=${v}`).join(' ');
        // eslint-disable-next-line no-console
        console.log(`[PlaybackSessionTracker][${tag}] ${flat}`);
    }
}

const tracker = new PlaybackSessionTracker();
export default tracker;
export { SessionEndReason };
